import { useState } from "react";
import { Card, CardBody } from "reactstrap";
import WizardPanel from "../WizardPanel";
import { AsyncButton } from "../../../common/buttons";
import { FONT_MONO, SEPARATOR_BORDER } from "../wizardStyles";
import { verifyPortForwarding } from "../../../../services/installationService";
import { captureWizardError } from "../shared/wizardObservability";

const valueOrDash = (v) => (v === null || v === undefined || v === "" ? "—" : String(v));

function describeError(err) {
  if (err?.code === "ECONNABORTED") return "Check timed out";
  if (!err?.response) return "Network error";
  if (err.response.status >= 500) return "Server error, please retry";
  return err.response.data?.detail ?? "Check failed";
}

function MappingRow({ label, internalIp, internalPort, externalIp, externalPort }) {
  return (
    <div
      className="d-flex align-items-center justify-content-between px-3 py-2 small"
      style={{ gap: "1rem", borderTop: SEPARATOR_BORDER }}
    >
      <span className="text-muted">{label}</span>
      <span className="d-flex align-items-center" style={{ fontFamily: FONT_MONO, gap: "0.35rem" }}>
        <span className="text-muted">Internal</span>
        <strong>
          {valueOrDash(internalIp)}:{valueOrDash(internalPort)}
        </strong>
        <span className="text-muted">→</span>
        <span className="text-muted">External</span>
        <strong>
          {valueOrDash(externalIp)}:{valueOrDash(externalPort)}
        </strong>
      </span>
    </div>
  );
}

function DvrResult({ result }) {
  if (!result) return null;
  const { http_port_open: httpOpen, rtsp_port_open: rtspOpen, success } = result;
  const color = success ? "text-success" : "text-danger";
  const icon = success ? "fa-check-circle" : "fa-times-circle";
  return (
    <small className={`${color} px-3 py-2 d-block`}>
      <i className={`fas ${icon} mr-1`} />
      HTTP {httpOpen ? "open" : "closed"}, RTSP {rtspOpen ? "open" : "closed"}
    </small>
  );
}

function DvrMappingCard({ dvr, index, wanIp, result }) {
  const internalIp = dvr?.dvr_ipv4;
  return (
    <Card className="mb-3">
      <CardBody className="p-0">
        <div
          className="d-flex align-items-center justify-content-between px-3 py-2 small text-muted text-uppercase font-weight-bold"
          style={{ borderBottom: SEPARATOR_BORDER }}
        >
          <span>DVR {index + 1}</span>
          <span className="text-reset" style={{ fontFamily: FONT_MONO, textTransform: "none" }}>
            Internal {internalIp || "—"} · Public {wanIp || "—"}
          </span>
        </div>
        <MappingRow
          label="Veesion HTTP access"
          internalIp={internalIp}
          internalPort={dvr?.dvr_http_port}
          externalIp={wanIp}
          externalPort={dvr?.dvr_external_http_port}
        />
        <MappingRow
          label="Veesion RTSP access"
          internalIp={internalIp}
          internalPort={dvr?.dvr_rtsp_port}
          externalIp={wanIp}
          externalPort={dvr?.dvr_external_rtsp_port}
        />
        <DvrResult result={result} />
      </CardBody>
    </Card>
  );
}

/**
 * @param {import("../WizardCard").StepPanelProps} props
 *
 * Per-DVR port-forwarding rules (read-only) + a button that probes all DVRs.
 */
export default function PortForwardingPanel({ installation, dvrStore, status, bottomNav }) {
  const identifier = installation?.uid ?? installation?.deal_id;
  const persistedDvrs = (dvrStore.dvrs ?? []).filter((d) => d.id != null);
  const canCheck = Boolean(
    identifier &&
      installation?.wan_ipv4 &&
      persistedDvrs.length > 0 &&
      persistedDvrs.every((d) => d.dvr_external_http_port && d.dvr_external_rtsp_port)
  );
  // Map dvr_id → per-DVR verify result.
  const [resultsByDvr, setResultsByDvr] = useState(null);
  const [error, setError] = useState(null);

  const handleCheck = async () => {
    if (!canCheck) return;
    setResultsByDvr(null);
    setError(null);
    try {
      const { data } = await verifyPortForwarding(identifier);
      const byDvr = {};
      (data?.dvrs ?? []).forEach((row) => {
        if (row?.dvr_id != null) byDvr[row.dvr_id] = row;
      });
      setResultsByDvr(byDvr);
    } catch (err) {
      captureWizardError(err, "verifyPortForwarding", { identifier });
      setError(describeError(err));
    }
  };

  return (
    <WizardPanel
      title="Port Forwarding"
      subtitle="Apply these rules on the on-site router"
      status={status}
      bottomNav={bottomNav}
    >
      {persistedDvrs.length === 0 ? (
        <Card className="mb-3">
          <CardBody className="text-muted">Create a DVR on the Video Recorder step first.</CardBody>
        </Card>
      ) : (
        persistedDvrs.map((dvr, index) => (
          <DvrMappingCard
            key={dvr.id}
            dvr={dvr}
            index={index}
            wanIp={installation?.wan_ipv4}
            result={resultsByDvr?.[dvr.id]}
          />
        ))
      )}

      <div className="d-flex align-items-center" style={{ gap: "0.6rem" }}>
        <AsyncButton color="primary" outline disabled={!canCheck} onClick={handleCheck}>
          <i className="fas fa-play mr-2" />
          Check Port Forwarding
        </AsyncButton>
        {!canCheck && <small className="text-muted">Fill in WAN IP and every DVR&apos;s external ports first</small>}
        {error && (
          <small className="text-danger">
            <i className="fas fa-times-circle mr-1" />
            {error}
          </small>
        )}
      </div>
    </WizardPanel>
  );
}
